import { useVehiclesStore } from '~/stores/vehicles'
import { useFrontPageStore } from '~/stores/frontPage'
import { useVehiclesPageFilters } from '~/stores/vehiclesPageFilters'
import { ref, readonly, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useNuxtApp } from '#imports' // Nuxt specific import
import { synchronizeFilterParams, initializeFilterSync, restoreUrlState } from './useFilterSync'
// Import core types from the central location
import type { FilterData, FilterOption } from '~/types'

// NOTE: FilterData and FilterOption should be imported directly from types

/**
 * Define the structure for a filter, combining data and UI config
 * This interface should align with the types used in the store and components
 */
export interface Filter {
  name: string;
  displayName: string;
  type: 'checkbox' | 'slider' | 'text' | 'buttons' | 'grid' | 'color-swatches' | 'boolean';
  options?: FilterOption[]; // Add optional options for array types
  min?: number;             // Add optional min for range types
  max?: number;             // Add optional max for range types
  data?: any; // Keep for potential other uses or specific data payloads
  uiConfig?: {
    displayType?: string;
    [key: string]: any;
  };
}

// Define the structure for a filter section
export interface FilterSection {
  id: string;
  title: string;
  expanded: boolean;
  filters: string[];
}

// Define interface for the filter sync plugin
interface FilterSyncPlugin {
  updateURL: () => void;
  applyFiltersFromURL: (query: any) => boolean;
  markUserInteraction: (filterName: string) => void;
  clearURLParameters: () => void;
  hasInteractedWithAnyFilter: boolean;
}

// Create a reactive reference to store the singleton
// Use proper typing to avoid TypeScript errors
interface FilterInteractionMethods {
  trackFilterInteraction: (filterName: string) => void;
  hasInteractedWith: (filterName: string) => boolean;
  clearInteraction: (filterName: string) => void;
  clearAllInteractions: () => void;
  userHasInteractedWithAnyFilter: any; // Using 'any' to avoid computed ref typing issues
  interactedFilters: any;
  initFromUrl: () => void;
  markInitializationComplete: () => void;
  isInitializing: any;
}

const filterInteractionState = ref<FilterInteractionMethods | null>(null);

/**
 * Composable to track which filters the user has interacted with
 * This is now a wrapper around the useFilterSync composable
 */
export function useFilterInteraction() {
  // Only create the instance if we're in a proper context
  if (process.client && !filterInteractionState.value) {
    try {
      // Get the singleton instance from the plugin
      const nuxtApp = useNuxtApp();

      // Check if filterSync exists and has the required methods
      if (nuxtApp && nuxtApp.$filterSync) {
        const filterSync = nuxtApp.$filterSync as FilterSyncPlugin;

        // Create the filter interaction methods with safe method calls
        filterInteractionState.value = {
          trackFilterInteraction: (filterName: string) => {
            if (filterSync && typeof filterSync.markUserInteraction === 'function') {
              filterSync.markUserInteraction(filterName);
            } else {
              // console.warn(\`[useFilterInteraction] markUserInteraction is not available on filterSync\`);
            }
          },
          hasInteractedWith: (filterName: string) => false, // Placeholder until we have proper access
          clearInteraction: (filterName: string) => {
            // Call the underlying filterSync clearInteraction if it exists
            if (filterSync && typeof (filterSync as any).clearInteraction === 'function') {
              (filterSync as any).clearInteraction(filterName);
            }
          },
          clearAllInteractions: () => {
            if (filterSync && typeof filterSync.clearURLParameters === 'function') {
              filterSync.clearURLParameters();
            }
          },
          userHasInteractedWithAnyFilter: computed(() =>
            filterSync && typeof filterSync.hasInteractedWithAnyFilter !== 'undefined'
              ? filterSync.hasInteractedWithAnyFilter
              : false
          ),
          interactedFilters: computed(() => new Set()), // Compatibility with old code
          initFromUrl: () => {}, // No-op, handled by filterSync
          markInitializationComplete: () => {}, // No-op, handled by filterSync
          isInitializing: computed(() => false) // Placeholder until we have proper access
        };
      } else {
        // Create fallback implementation
        // console.warn('[useFilterInteraction] $filterSync is not available, using fallback implementation');
        const interactedFiltersSet = ref(new Set<string>());
        const userHasInteracted = ref(false);

        filterInteractionState.value = {
          trackFilterInteraction: (filterName: string) => {
            interactedFiltersSet.value.add(filterName);
            userHasInteracted.value = true;
          },
          hasInteractedWith: (filterName: string) => interactedFiltersSet.value.has(filterName),
          clearInteraction: (filterName: string) => {
            interactedFiltersSet.value.delete(filterName);
            if (interactedFiltersSet.value.size === 0) {
              userHasInteracted.value = false;
            }
          },
          clearAllInteractions: () => {
            interactedFiltersSet.value.clear();
            userHasInteracted.value = false;
          },
          userHasInteractedWithAnyFilter: computed(() => userHasInteracted.value),
          interactedFilters: computed(() => interactedFiltersSet.value),
          initFromUrl: () => {},
          markInitializationComplete: () => {},
          isInitializing: computed(() => false)
        };
      }
    } catch (error) {
      // console.error('[useFilterInteraction] Error creating filter interaction singleton:', error);

      // Create fallback implementation on error
      const interactedFiltersSet = ref(new Set<string>());
      const userHasInteracted = ref(false);

      filterInteractionState.value = {
        trackFilterInteraction: (filterName: string) => {
          interactedFiltersSet.value.add(filterName);
          userHasInteracted.value = true;
        },
        hasInteractedWith: (filterName: string) => interactedFiltersSet.value.has(filterName),
        clearInteraction: (filterName: string) => {
          interactedFiltersSet.value.delete(filterName);
          if (interactedFiltersSet.value.size === 0) {
            userHasInteracted.value = false;
          }
        },
        clearAllInteractions: () => {
          interactedFiltersSet.value.clear();
          userHasInteracted.value = false;
        },
        userHasInteractedWithAnyFilter: computed(() => userHasInteracted.value),
        interactedFilters: computed(() => interactedFiltersSet.value),
        initFromUrl: () => {},
        markInitializationComplete: () => {},
        isInitializing: computed(() => false)
      };
    }
  }

  // Return a default implementation if the singleton isn't available
  if (!filterInteractionState.value) {
    // Create a completely isolated fallback if somehow filterInteractionState is still null
    const interactedFiltersSet = ref(new Set<string>());
    const userHasInteracted = ref(false);

    return {
      trackFilterInteraction: (filterName: string) => {
        interactedFiltersSet.value.add(filterName);
        userHasInteracted.value = true;
      },
      hasInteractedWith: (filterName: string) => interactedFiltersSet.value.has(filterName),
      clearInteraction: (filterName: string) => {
        interactedFiltersSet.value.delete(filterName);
        if (interactedFiltersSet.value.size === 0) {
          userHasInteracted.value = false;
        }
      },
      clearAllInteractions: () => {
        interactedFiltersSet.value.clear();
        userHasInteracted.value = false;
      },
      userHasInteractedWithAnyFilter: computed(() => userHasInteracted.value),
      interactedFilters: computed(() => interactedFiltersSet.value),
      initFromUrl: () => {},
      markInitializationComplete: () => {},
      isInitializing: computed(() => false)
    };
  }

  return filterInteractionState.value;
}

// Export a function to get the singleton for use in other composables
export function getFilterInteractionSingleton() {
  return useFilterInteraction();
}

// Export a function to get the singleton instance
export function getFilterInteraction() {
  return useFilterInteraction();
}

// Create these refs inside a function to avoid module-level Nuxt app access
function createGlobalRefs() {
  // These will be initialized when needed in component context
  return {
    urlState: ref({
      isRestoring: false,
      lastQuery: {} as Record<string, string | string[] | undefined>
    }),
    isInternalUrlChange: ref(false)
  };
}

// Initialize the global refs
const globalRefs = createGlobalRefs();
const urlState = globalRefs.urlState;
const isInternalUrlChange = globalRefs.isInternalUrlChange;

// Define URL state management functions
function updateURLState(queryString: string) {
  if (urlState && urlState.value) {
    urlState.value.lastQuery = parseQueryString(queryString);
  }
}

function setInternalUrlChange(value: boolean) {
  if (isInternalUrlChange) {
    isInternalUrlChange.value = value;
  }
}

// Generate query string from current filters
const generateQueryString = (filters: Record<string, any>): string => {
  const queryParams: string[] = []

  Object.entries(filters).forEach(([key, value]) => {
    if (!value) return

    if (value.type === 'range') {
      // For range filters, combine min and max into a single parameter
      if (value.min !== undefined && value.max !== undefined) {
        queryParams.push(`${key}=${value.min}-${value.max}`)
      }
    } else if (value.type === 'array') {
      if (value.values?.length) {
        queryParams.push(`${key}=${encodeURIComponent(value.values.join(','))}`)
      }
    } else if (value.type === 'text') {
      if (value.value) {
        queryParams.push(`${key}=${encodeURIComponent(value.value)}`)
      }
    } else if (value.type === 'boolean') {
      if (value.value) {
        queryParams.push(`${key}=true`)
      }
    }
  })

  return queryParams.join('&')
}

// Parse query string into object
const parseQueryString = (query: string): Record<string, string | string[] | undefined> => {
  if (!query) return {}

  const params = new URLSearchParams(query)
  const result: Record<string, string | string[] | undefined> = {}

  for (const [key, value] of params.entries()) {
    if (value.includes('-')) {
      // Handle range filters (e.g., price=1000-2000)
      result[key] = value
    } else if (value.includes(',')) {
      // Handle array filters (e.g., make=toyota,honda)
      result[key] = value.split(',').filter(Boolean)
    } else {
      result[key] = value
    }
  }

  return result
}

// Add initialization function
export function initializeFilters() {
  if (process.client) {
    try {
      initializeFilterSync()
    } catch (error) {
      // console.error('[useFilters] Error initializing filters:', error)
    }
  }
}

export function useFilters() {
  const nuxtApp = useNuxtApp();
  const vehiclesStore = useVehiclesStore();
  const frontPageStore = useFrontPageStore();
  const vehiclesPageFilters = useVehiclesPageFilters();
  const filterInteraction = useFilterInteraction();

  // Get the filter sync plugin
  const filterSync = nuxtApp.$filterSync;

  // Create default filter interaction methods
  const defaultFilterInteraction: FilterInteractionMethods = {
    trackFilterInteraction: () => {},
    hasInteractedWith: () => false,
    clearInteraction: (filterName: string) => {},
    clearAllInteractions: () => {},
    userHasInteractedWithAnyFilter: computed(() => false),
    interactedFilters: computed(() => new Set()),
    initFromUrl: () => {},
    markInitializationComplete: () => {},
    isInitializing: computed(() => false)
  };

  // Get filter by name - Rewritten to use availableFilters from store state
  const getFilter = (name: string): Filter | null => {
    try {
      // Check if store data is loaded
      if (!vehiclesStore.isDataLoaded || !vehiclesStore.availableFilters) {
        // console.warn(\`[useFilters] Filter '${name}' requested but store data not loaded.\`);
        return null;
      }

      const available = vehiclesStore.availableFilters;
      const displayName = name.charAt(0).toUpperCase() + name.slice(1); // Simple display name generation

      // Base filter structure
      const filter: Filter = {
        name: name,
        displayName: displayName, // Placeholder, can be refined
        type: 'checkbox', // Default type
        uiConfig: {} // Initialize uiConfig
      };

      // Determine type and populate data based on filter name
      if (['make', 'model', 'badge', 'body', 'condition', 'transmission', 'drivetrain', 'fuel', 'seats', 'doors', 'colour'].includes(name)) {
        const optionsArray = available[name as keyof typeof available] as string[] | undefined;
        if (Array.isArray(optionsArray)) {
          filter.type = 'checkbox'; // Assuming checkbox for these array types
          // Map string array to FilterOption array
          filter.options = optionsArray.map(value => {
            // Extract make value from model/badge value using underscore separator only
            // (e.g., "gwm_cannon-alpha" -> makeValue "gwm"). Some badges don't have a make prefix.
            const underscoreParts = value.split('_');
            const makeValue = underscoreParts.length > 1 ? underscoreParts[0] : '';

            // Transform slug to human-readable displayValue
            // Support both '_' and '-' separators and common acronyms/short codes
            const knownAcronyms = [
              'GWM', 'BMW', 'LDV', 'MG', 'SKODA', 'HSV', 'FOTON', 'RAM', 'SUV',
              'GT', 'RS', 'XR', 'ST', 'XLT', 'LTZ', 'ZR2'
            ];
            const tokens = value
              .replace(/[_-]+/g, ' ') // normalize separators
              .split(' ')
              .filter(Boolean);
            let displayValue = tokens
              .map(part => {
                const upperPart = part.toUpperCase();
                if (knownAcronyms.includes(upperPart)) return upperPart;
                if (/^\d+$/.test(part)) return part; // keep pure numbers
                if (part.length <= 3) return upperPart; // short codes like gt, rs
                if (part.toLowerCase() === 'ute') return 'Ute';
                return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
              })
              .join(' ');

            // Special case for bare make values (no separators)
            if (name === 'make' && underscoreParts.length === 1 && underscoreParts[0]) {
              const upperPart = underscoreParts[0].toUpperCase();
              if (knownAcronyms.includes(upperPart)) {
                displayValue = upperPart;
              } else {
                displayValue = underscoreParts[0].charAt(0).toUpperCase() + underscoreParts[0].slice(1).toLowerCase();
              }
            }

            return {
              value: value,
              displayValue: displayValue,
              makeValue: makeValue
            } as FilterOption;
          });
          // Assign displayMake based on makeValue for consistency (can be used by components)
          // This replaces the previous inefficient vehicle lookup
           if (name === 'model' || name === 'badge') {
               filter.options.forEach(option => {
                   option.displayMake = option.makeValue ? getFilterOptionLabel('make', option.makeValue) : '';
               });
           }

        } else {
           // console.warn(\`[useFilters] Expected array for filter '${name}' but got:\`, optionsArray);
           return null; // Filter data is not in the expected format
        }
      } else if (name === 'price' && available.priceRange) {
        filter.type = 'slider';
        filter.displayName = 'Price'; // Specific display name
        filter.min = available.priceRange.min;
        filter.max = available.priceRange.max;
        filter.data = { min: filter.min, max: filter.max }; // Pass data for slider component
      } else if (name === 'kms' && available.kmsRange) {
        filter.type = 'slider';
        filter.displayName = 'Kilometers';
        filter.min = available.kmsRange.min;
        filter.max = available.kmsRange.max;
        filter.data = { min: filter.min, max: filter.max };
      } else if (name === 'year' && available.yearRange) {
         filter.type = 'slider';
         filter.displayName = 'Year';
         filter.min = available.yearRange.min;
         filter.max = available.yearRange.max;
         filter.data = { min: filter.min, max: filter.max };
      } else if (name === 'perweek' && available.perweekRange) {
         filter.type = 'slider';
         filter.displayName = 'Weekly Payment';
         filter.min = available.perweekRange.min;
         filter.max = available.perweekRange.max;
         filter.data = { min: filter.min, max: filter.max };
      } else if (name === 'perweek' && !available.perweekRange) {
         // Create a default perweek filter to avoid errors
         filter.type = 'slider';
         filter.displayName = 'Weekly Payment';
         filter.min = 50;
         filter.max = 500;
         filter.data = { min: filter.min, max: filter.max };
      } else if (name === 'search_keywords') {
        filter.type = 'text';
        filter.displayName = 'Search';
      } else if (name === 'favorites') {
        filter.type = 'boolean';
        filter.displayName = 'Show Favorites';
      } else {
        // console.log(\`[useFilters] Filter definition not found for '${name}'.\`);
        return null; // Unknown filter name
      }

      // Set default displayType in uiConfig
      filter.uiConfig = filter.uiConfig || {};
      filter.uiConfig.displayType = filter.uiConfig.displayType || filter.type;

      // TODO: Integrate UI schema definitions from store or config if available
      // Example: const uiSchema = vehiclesStore.uiSchema; find section/filter config

      return filter;

    } catch (error) {
      // console.error(\`[useFilters] Error in getFilter for '${name}':\`, error);
      return null;
    }
  }

  // Helper to determine if a filter should be displayed
  const shouldDisplayFilter = (filterName: string, filterValue: any, forCount: boolean = false): boolean => {
    if (!filterValue) {
      return false;
    }

    // Special case: if favorites filter is active, only show the favorites filter
    if (filterName !== 'favorites' &&
        vehiclesStore.activeFilters.favorites &&
        vehiclesStore.activeFilters.favorites.type === 'boolean' &&
        vehiclesStore.activeFilters.favorites.value === true) {
      return false;
    }

    // Handle boolean filters first (like favorites)
    if (typeof filterValue === 'object' && filterValue !== null && filterValue.type === 'boolean') {
      const shouldShow = filterValue.value === true;
      return shouldShow;
    }
    // Also handle raw boolean if passed directly (should ideally not happen)
    else if (typeof filterValue === 'boolean') {
        const shouldShow = filterValue === true;
        return shouldShow;
    }

    // Handle structured array filters (new format)
    if (typeof filterValue === 'object' && filterValue !== null &&
        filterValue.type === 'array' && Array.isArray(filterValue.value)) {
      const shouldShow = filterValue.value.length > 0;
      return shouldShow;
    }
    // Handle raw array filters (older format?)
    else if (Array.isArray(filterValue)) {
        const shouldShow = filterValue.length > 0;
        return shouldShow;
    }

    // Handle range filters (structured object format)
    if (typeof filterValue === 'object' && filterValue !== null &&
        filterValue.type === 'range' && 'min' in filterValue && 'max' in filterValue) {
      const filter = getFilter(filterName)
      // Ensure we have the filter definition and its data to compare against defaults
      if (filter?.data && filter.type === 'slider') {
        const data = filter.data as any // Cast needed as data type varies
        const defaultMin = Number(data.min) || 0
        const defaultMax = Number(data.max) || 1000000 // Use a high default max if needed
        const currentMin = Number(filterValue.min)
        const currentMax = Number(filterValue.max)

        // Check if min OR max is different from the default values
        const isActive = currentMin !== defaultMin || currentMax !== defaultMax;
        return isActive;
      } else {
        // If filter definition or data is missing, cannot determine if it's default.
        // Assume active for safety, or return false if filters must be defined.
        return true;
      }
    }

    // If none of the above conditions match, assume the filter should be displayed
    return true;
  }

  // Helper to get filter option count
  const getFilterOptionCount = (filterName: string, optionValue: string): number => {
    try {
      // Use the correct getter name: filterOptionCounts
      if (!vehiclesStore.filterOptionCounts) {
        return 0;
      }

      // Safely access the filter counts with type assertion
      const filterCountsForName = (vehiclesStore.filterOptionCounts as any)[filterName] || {};
      return filterCountsForName[optionValue] || 0;
    } catch (error) {
      // console.error('[useFilters] Error getting filter option count:', error);
      return 0;
    }
  }

  // Helper to check if a filter is in a section
  const hasFilterInSection = (filterName: string, section: FilterSection): boolean => {
    return section.filters.includes(filterName) && !!getFilter(filterName)
  }

  // Helper to count active filters
  const countActiveFilters = (activeFilters: Record<string, any>): number => {
    let count = 0

    for (const [filterName, filterValue] of Object.entries(activeFilters)) {
      if (shouldDisplayFilter(filterName, filterValue, true)) {
        count++
      }
    }

    return count
  }

  // Helper to get filter display name
  const getFilterDisplayName = (filterName: string, filterValue: any): string => {
    const filter = getFilter(filterName)
    // If filter definition is missing, just return the raw filter name
    if (!filter) return filterName

    // Handle array filters
    if (filter.type === 'checkbox' && filter.options) {
      const activeOptions = Array.isArray(filterValue?.value) ? filterValue.value : (Array.isArray(filterValue) ? filterValue : []);
      if (activeOptions.length === 0) return filter.displayName;
      if (activeOptions.length === 1) {
        const option = filter.options.find(opt => opt.value === activeOptions[0]);
        return option ? option.displayValue : activeOptions[0];
      }
      return `${activeOptions.length} selected`;
    }

    // Handle range/slider filters
    if (filter.type === 'slider') {
       const currentMin = filterValue?.min ?? filter.min;
       const currentMax = filterValue?.max ?? filter.max;
       if (currentMin === filter.min && currentMax === filter.max) {
         return filter.displayName; // Show only name if it's the default range
       }
       // Format range display (consider using Intl.NumberFormat for currency/kms)
       return `${currentMin} - ${currentMax}`;
    }

    // Handle boolean filters
    if (filter.type === 'boolean') {
      return (filterValue?.value ?? filterValue) ? filter.displayName : ''
    }

    // Handle text filters
    if (filter.type === 'text') {
      return (filterValue?.value || filterValue || '').trim() || filter.displayName;
    }

    // Fallback display name
    return filter.displayName;
  }

  // Helper to get filter option label
  const getFilterOptionLabel = (filterName: string, optionValue: string): string => {
    const filter = getFilter(filterName)
    if (!filter || !filter.options) return optionValue // Check for options array

    const option = filter.options.find(opt => opt.value === optionValue)
    return option ? option.displayValue : optionValue
  }

  return {
    // Re-export filter interaction methods
    trackFilterInteraction: filterInteraction.trackFilterInteraction,
    clearInteraction: filterInteraction.clearInteraction,
    clearAllInteractions: filterInteraction.clearAllInteractions,
    userHasInteractedWithAnyFilter: filterInteraction.userHasInteractedWithAnyFilter,

    // Re-export URL synchronization methods - use imported functions directly
    synchronizeFilterParams,

    // Filter utility methods
    getFilter,
    shouldDisplayFilter,
    getFilterOptionCount,
    hasFilterInSection,
    countActiveFilters,
    getFilterDisplayName,
    getFilterOptionLabel,

    // Compatibility with old code
    initFilterInteractionsFromUrl: () => {}, // No-op, handled by filterSync
    initFromUrl: () => {}, // No-op, handled by filterSync
    isInitializing: filterInteraction.isInitializing,
    urlState,
    isInternalUrlChange,
    updateURLState,
    setInternalUrlChange,
    generateQueryString,
    parseQueryString,
    restoreUrlState
  }
}
